import React, { Dispatch, SetStateAction, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useAppDispatch, useAppSelector } from "src/redux/store";
import {
  Box,
  Button,
  Chip,
  FormHelperText,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import DeleteIcon from "@mui/icons-material/Delete";
// import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { quillModules } from "./EmailDetails";
import "./emailEditor.scss";
import { SendEmail } from "src/redux/features/mailBox/mailbox-slice";
import { IMail } from "src/types/mail";

interface IEmailEditor {
  isDraft: boolean;
  isDialogView: boolean;
  composeToEmail?: string;
  setComposeToEmail?: Dispatch<SetStateAction<string>>;
  currentDraft?: IMail | null;
  closeDialog?: Dispatch<SetStateAction<boolean>>;
}

const EmailEditor: React.FC<IEmailEditor> = ({
  isDraft,
  isDialogView,
  composeToEmail,
  setComposeToEmail,
  currentDraft,
  closeDialog,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [emailBody, setEmailBody] = useState<string>(
    currentDraft && currentDraft.body ? currentDraft.body : ""
  );
  const [attachments, setAttachments] = useState<File[]>([]);
  const [subject, setSubject] = useState<string | undefined>(
    currentDraft ? currentDraft?.subject : ""
  );
  const [emptyError, setEmptyError] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<boolean>(false);
  // const [openDialog, setOpenDialog] = useState<boolean>(false);
  // const [deleteDraft, setDeleteDraft] = useState<boolean>(false);

  const mailBoxAuthData = useAppSelector(
    (state) => state.mailBoxSlice.mailBoxAuthData
  );

  const [to, setTo] = useState<string[]>(() => {
    if (isDraft && currentDraft?.to) {
      return currentDraft.to.map((recipient) => recipient.email);
    }
    if (composeToEmail) {
      return [composeToEmail];
    }
    return [];
  });

  const [inputValue, setInputValue] = useState("");

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === "Enter" || e.key === ",") && inputValue.trim()) {
      e.preventDefault();
      const newEmail = inputValue.trim().replace(/,$/, "");
      if (validateEmail(newEmail) && !to.includes(newEmail)) {
        setTo([...to, newEmail]);
        setInputValue("");
        return;
      } else {
        setValidationError(true);
        return;
      }
    }
  };

  const handleDelete = (emailToDelete: string) => {
    setTo(to.filter((email) => email !== emailToDelete));
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setAttachments([...attachments, ...Array.from(event.target.files)]);
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };
  const validateEmail = (email: string) => {
    return /\S+@\S+\.\S+/.test(email);
  };
  // const validateEmail = () => {
  //   const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  //   return emailRegex.test(to);
  // };
  const formatEmailBody = (body: string) => {
    return `
          <div style="margin: 0; padding: 0;">
            ${body.replace(
              /<(p|h[1-6])>/g,
              '<$1 style="margin: 0; padding: 0;">'
            )}
          </div>
        `;
  };

  const handleSend = async () => {
    if (!mailBoxAuthData) {
      return;
    }

    if (to.length === 0 && !inputValue.trim()) {
      setEmptyError(true);
      return;
    }
    if (inputValue.trim() && !validateEmail(inputValue) && to.length === 0) {
      setValidationError(true);
      return;
    }
    const formatted = formatEmailBody(emailBody); // remove margins form the <p> and <h> tags
    await dispatch(
      SendEmail({
        email: mailBoxAuthData.email,
        to: to.length === 0 ? [inputValue.trim()] : to,
        subject,
        body: formatted,
        attachments,
      })
    );
    if (isDialogView && closeDialog) {
      closeDialog(false);
      if (setComposeToEmail) {
        setComposeToEmail("");
      }
    } else {
      navigate("/mailbox");
    }
  };

  //De-scope Draft feature
  // const saveDraft = () => {
  //   if (!user.email) {
  //     return;
  //   }
  //   const formatted = formatEmailBody(emailBody); // remove margins form the <p> and <h> tags
  //   dispatch(
  //     createDraft({
  //       email: user?.email as string,
  //       to: [to],
  //       subject,
  //       body: formatted,
  //       attachments,
  //     })
  //   );
  //   navigate("/mailbox");
  // };

  // const updatedDraft = () => {
  //   if (!user.email) {
  //     return;
  //   }
  //   dispatch(
  //     UpdateDraft({
  //       draftId: currentDraft?.id as string,
  //       draftDetails: {
  //         to: [to],
  //         subject,
  //         body: emailBody,
  //         attachments,
  //         email: user?.email,
  //       },
  //     })
  //   );
  //   navigate("/mailbox");
  // };

  // const hasUnsavedChanges = () => {
  //   if (isDraft && currentDraft) {
  //     return (
  //       to !== currentDraft.to[0].email ||
  //       subject !== currentDraft.subject ||
  //       emailBody !== currentDraft.body ||
  //       attachments.length !== currentDraft.attachments.length
  //     );
  //   } else {
  //     return Boolean(to || subject || emailBody || attachments.length);
  //   }
  // };

  const handleBack = () => {
    // if (hasUnsavedChanges()) {
    //   setOpenDialog(true);
    //   return;
    // }
    navigate("/mailbox");
  };

  return (
    <div className="emailEditor">
      <div className="emailEditor__content">
      <Button
        variant="text"
        className="emailEditor__backBtn"
        onClick={handleBack}
      >
        <KeyboardBackspaceIcon />
        <Typography variant="h6" className="emailEditor__backBtn-text">
          Back
        </Typography>
      </Button>

      <Typography variant="h4" className="emailEditor__title">
        Compose Mail
      </Typography>
      <div className="emailEditor__senderName">
        <Typography className="label">To</Typography>
        {/* <div> */}
        {/* <TextField
          fullWidth
          className="emailEditor__senderName-textField"
          id="fullWidth"
          sx={{
            "& fieldset": { border: "none" },
          }}
          value={to}
          onChange={(e) => {setTo(e.target.value);
            setValidationError(false);
          }}
        />

        {/* </div> */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1,
            border: "1px solid #ccc",
            borderRadius: 1,
            padding: 1,
            minHeight: "56px",
            width: "100%",
          }}
          onClick={() => document.getElementById("email-input")?.focus()}
        >
          {to.map((email) => (
            <Chip
              key={email}
              label={email}
              onDelete={() => handleDelete(email)}
              sx={{ margin: "2px" }}
            />
          ))}
          <TextField
            id="email-input"
            variant="standard"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              if (validationError) {
                setValidationError(false);
              }
            }}
            onKeyDown={handleKeyDown}
            sx={{ flexGrow: 1, "& input": { padding: 0 } }}
            InputProps={{
              disableUnderline: true,
            }}
            placeholder={to.length === 0 ? "To" : ""}
          />
        </Box>
      </div>
      {emptyError && !to && (
        <FormHelperText className="error-msg">
          Please enter recipent email.
        </FormHelperText>
      )}
      {validationError && to && (
        <FormHelperText className="error-msg">
          Please enter valid email.
        </FormHelperText>
      )}
      <div className="emailEditor__subject">
        <Typography className="label">Subject</Typography>
        <TextField
          fullWidth
          placeholder="Enter Subject"
          className="emailEditor__subject-textField"
          sx={{
            "& fieldset": { border: "none" },
          }}
          id="fullWidth"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />
      </div>
      <Box className="email-Editor">
        <ReactQuill
          value={emailBody}
          onChange={setEmailBody}
          modules={quillModules}
          placeholder="Compose your email..."
        />
        <Box className="email-attachments">
          {attachments.map((file, index) => (
            <Box key={index} className="email-attachment">
              <Typography variant="body2" className="attachment-name">
                {file.name}
              </Typography>
              <IconButton size="small" onClick={() => removeAttachment(index)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>

        {/* Attachment Section */}
      </Box>
      </div>
      <div className="editor-footer">
        <div className="flex-gap">
          <Button variant="contained" color="primary" onClick={handleSend}>
            Send
          </Button>
          <input
            type="file"
            multiple
            onChange={handleFileChange}
            style={{ display: "none" }}
            id="file-upload"
          />
          <label htmlFor="file-upload">
            <IconButton color="primary" component="span">
              <AttachFileIcon />
            </IconButton>
          </label>
        </div>
        {/* {isDraft && (
            <DeleteOutlinedIcon
              className="icon"
              onClick={() => setDeleteDraft(true)}
            />
          )} */}
      </div>
      {/* <Dialog open={openDialog} className="smallModel">
        <div>
          {isDraft ? (
            <DialogContent>Do you want to update draft?</DialogContent>
          ) : (
            <DialogContent>Do you want to save draft?</DialogContent>
          )}
          <DialogActions>
            <Button
              className="border-button"
              variant="contained"
              onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                e.preventDefault();
                setOpenDialog(false);
                navigate("/mailbox");
              }}
              color="secondary"
            >
              No
            </Button>
            <Button
              className="button"
              variant="contained"
              onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                e.preventDefault();
                if (isDraft) {
                  updatedDraft();
                } else {
                  saveDraft();
                }
              }}
            >
              Yes
            </Button>
          </DialogActions>
        </div>
      </Dialog> */}

      {/* <Dialog open={deleteDraft} className="smallModel">
        <div>
          <DialogContent>Do you want to Delete draft?</DialogContent>
          <DialogActions>
            <Button
              className="border-button"
              variant="contained"
              onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                e.preventDefault();
                setDeleteDraft(false);
              }}
              color="secondary"
            >
              No
            </Button>
            <Button
              className="button"
              variant="contained"
              onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                e.preventDefault();
                if (currentDraft && user.email) {
                  dispatch(
                    DeleteDraftById({
                      email: user.email,
                      draftId: currentDraft.id,
                    })
                  );
                  navigate("/mailbox");
                }
              }}
            >
              Yes
            </Button>
          </DialogActions>
        </div>
      </Dialog> */}
    </div>
  );
};

export default EmailEditor;
